import React from "react";
import { Link } from "react-router-dom";
import { COURSES, COURSE_CATEGORIES } from "@/site";
import PageHero from "@/components/sections/PageHero";
import CTABanner from "@/components/sections/CTABanner";
import SEO, { localBusinessSchema } from "@/components/SEO";

export default function Courses() {
    const groups = COURSE_CATEGORIES.map((cat) => ({
        ...cat,
        items: COURSES.filter((c) => c.category === cat.key),
    }));

    return (
        <div data-testid="page-courses">
            <SEO
                title="All Courses | Music, Production, DJ & Dance Classes in Delhi | Ihao"
                description="Explore all 12 courses at Ihao Music House Delhi — instrument classes, vocal training, music production, DJ and dance. Certified curriculum. Book a free trial."
                path="/courses"
                schema={localBusinessSchema()}
            />

            <PageHero
                eyebrow="12 Courses · 4 Disciplines · All Levels"
                title="Find your instrument. Find your voice. Find your rhythm."
                subtitle="From first-time beginners to grade-certified performers — every Ihao course is built on international curriculum, performance opportunities and personal attention."
            />

            {groups.map((cat, gi) => (
                <section key={cat.key} data-testid={`courses-category-${cat.key}`} className={`relative py-20 md:py-28 ${gi % 2 === 0 ? "bg-background" : "bg-black"}`}>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                            <div>
                                <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Category {String(gi + 1).padStart(2, "0")}</span>
                                <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white leading-tight">{cat.label}</h2>
                                <p className="mt-3 text-white/60 max-w-2xl leading-relaxed">{cat.desc}</p>
                            </div>
                            <Link to="/contact" className="text-sm text-gold hover:text-gold-hover border-b border-gold/40 pb-1 w-fit">Book a free trial →</Link>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {cat.items.map((c) => (
                                <Link key={c.slug} to={`/courses/${c.slug}`} data-testid={`course-card-${c.slug}`} className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#121212] hover:border-gold/40 transition-all duration-300 hover:-translate-y-1">
                                    <div className="aspect-[4/3] overflow-hidden bg-neutral-900">
                                        <img src={c.image} alt={`${c.name} classes at Ihao Music House Delhi`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                                    <div className="absolute top-4 right-4 flex gap-1.5">
                                        {c.levels.map((l) => (
                                            <span key={l} className="text-[9px] tracking-widest uppercase text-white/80 border border-white/20 bg-black/40 backdrop-blur rounded-full px-2 py-0.5">{l}</span>
                                        ))}
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 p-6">
                                        <h3 className="font-display text-2xl font-semibold text-white">{c.name}</h3>
                                        <p className="mt-1 text-xs text-gold/90 uppercase tracking-[0.2em]">{c.short}</p>
                                        <p className="mt-3 text-sm text-white/60 line-clamp-2">{c.blurb}</p>
                                        <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-gold">View Course <span className="w-6 h-px bg-gold transition-all group-hover:w-10" /></span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            ))}

            <CTABanner title="Not sure where to start?" subtitle="Book a free demo and we'll help you pick the right course and level." />
        </div>
    );
}
